import { useEffect, useState } from 'react';
import Search from 'lib/Search';
import { CodeNameModel } from 'components/common/types';
import { AsyncSelectLoadDataHandler } from 'lib/AsyncSelect/AsyncSelect.types';

const DEBOUNCE_MS = 1000;

interface LookupSearchPanelProps<T extends CodeNameModel> {
  filterKey: string;
  placeholder: string;
  loadData: AsyncSelectLoadDataHandler<T>;
  onSelect: (value: T) => void;
  dataTestId?: string;
}

export const LookupSearchPanel = <T extends CodeNameModel>({
  filterKey,
  placeholder,
  loadData,
  onSelect,
  dataTestId,
}: LookupSearchPanelProps<T>): React.ReactElement => {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!searchText) {
      setResults([]);
      setIsLoading(false);
      setHasError(false);

      return undefined;
    }

    setIsLoading(true);
    setHasError(false);

    let isCancelled = false;

    const timeout = setTimeout(async () => {
      try {
        const data = await loadData(filterKey, searchText);

        if (!isCancelled) {
          setResults(data);
        }
      } catch {
        if (!isCancelled) {
          setResults([]);
          setHasError(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timeout);
    };
  }, [searchText, filterKey, loadData]);

  const renderResults = (): React.ReactNode => {
    let resultText = '';

    if (hasError) {
      resultText = 'Unable to load results. Try again.';
    }

    if (isLoading) {
      resultText = `Searching for '${searchText}'`;
    }

    if (!searchText) {
      resultText = 'Type to search';
    }

    if (results.length === 0 && !isLoading && !hasError && searchText) {
      resultText = 'No results';
    }

    if (resultText) {
      return <p className="text-center text-neutral-600 px-5 py-2.5 body-base">{resultText}</p>;
    }

    return (
      <ul className="py-3">
        {results.map((option) => (
          <li key={option.code}>
            <button
              type="button"
              className="w-full px-4 py-3 text-left rounded-md hover:bg-neutral-200 hover:text-primary-950"
              onClick={() => onSelect(option)}
            >
              {option.name}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="flex max-h-[inherit] min-h-0 w-64 flex-col overflow-hidden bg-white border border-neutral-200 rounded-md shadow-dropdownMenu">
      <div className="flex shrink-0 items-center gap-2 px-3 py-2 border-b border-neutral-200">
        <div className="flex-1 min-w-0">
          <Search
            value={searchText}
            placeholder={placeholder}
            onChange={(value) => setSearchText(String(value ?? ''))}
            dataTestId={dataTestId}
          />
        </div>
      </div>
      <div className="min-h-0 overflow-y-auto">{renderResults()}</div>
    </div>
  );
};
